import { TFunction } from "@impero-com/common/utils/i18n";
import { rawPalette } from "@impero-com/ui/styles/theme.css";

import { FrictionBreakdownCounts } from "api/generated/model";

import { SupportedFrictionBreakdownView } from "./frictionBreakdownStorage";

interface FrictionBreakdownSeries {
    key: keyof FrictionBreakdownCounts | "inProgress" | "completed";
    label: string;
    color: string;
}

export interface FrictionBreakdownViewDefinition {
    label: (t: TFunction) => string;
    headlineCaption: (t: TFunction, count: number, range: string) => string;
    emptyTitle: (t: TFunction, range: string) => string;
    series: (t: TFunction) => FrictionBreakdownSeries[];
}

export const FRICTION_BREAKDOWN_VIEW_DEFINITIONS: Record<
    SupportedFrictionBreakdownView,
    FrictionBreakdownViewDefinition
> = {
    all: {
        label: (t) => t("Controls_All"),
        headlineCaption: (t, count, range) =>
            t("ControlHealth_FrictionBreakdown_HeadlineCaption", {
                count,
                range,
            }),
        emptyTitle: (t, _range) =>
            t("ControlHealth_FrictionBreakdown_Empty_Title"),
        series: (t) => [
            {
                key: "overdue",
                label: t("ControlResult_Health_Overdue"),
                color: rawPalette.activity.overdue,
            },
            {
                key: "rejected",
                label: t("ControlResult_Health_Rejected"),
                color: rawPalette.base.lightBlue[400],
            },
            {
                key: "reopened",
                label: t("ControlResult_Health_Reopened"),
                color: rawPalette.base.lightBlue[200],
            },
        ],
    },
    overdue: {
        label: (t) => t("ControlResult_Health_Overdue"),
        headlineCaption: (t, count, range) =>
            t("ControlHealth_FrictionBreakdown_Overdue_HeadlineCaption", {
                count,
                range,
            }),
        emptyTitle: (t, range) =>
            t("ControlHealth_FrictionBreakdown_Overdue_Empty_Title", { range }),
        series: (t) =>
            buildStateSeries(
                t("ControlHealth_FrictionBreakdown_Overdue_InProgress"),
                t("ControlHealth_FrictionBreakdown_Overdue_Completed"),
                {
                    inProgress: rawPalette.activity.overdue,
                    completed: rawPalette.activity.completedAfterDue,
                }
            ),
    },
    rejected: {
        label: (t) => t("ControlResult_Health_Rejected"),
        headlineCaption: (t, count, range) =>
            t("ControlHealth_FrictionBreakdown_Rejected_HeadlineCaption", {
                count,
                range,
            }),
        emptyTitle: (t, range) =>
            t("ControlHealth_FrictionBreakdown_Rejected_Empty_Title", {
                range,
            }),
        series: (t) =>
            buildStateSeries(
                t("ControlHealth_FrictionBreakdown_Rejected_InProgress"),
                t("ControlHealth_FrictionBreakdown_Rejected_Completed"),
                {
                    inProgress: rawPalette.base.lightBlue[400],
                    completed: rawPalette.base.lightBlue[200],
                }
            ),
    },
    reopened: {
        label: (t) => t("ControlResult_Health_Reopened"),
        headlineCaption: (t, count, range) =>
            t("ControlHealth_FrictionBreakdown_Reopened_HeadlineCaption", {
                count,
                range,
            }),
        emptyTitle: (t, range) =>
            t("ControlHealth_FrictionBreakdown_Reopened_Empty_Title", {
                range,
            }),
        series: (t) =>
            buildStateSeries(
                t("ControlHealth_FrictionBreakdown_Reopened_InProgress"),
                t("ControlHealth_FrictionBreakdown_Reopened_Completed"),
                {
                    inProgress: rawPalette.base.lightBlue[400],
                    completed: rawPalette.base.lightBlue[200],
                }
            ),
    },
};

function buildStateSeries(
    inProgressLabel: string,
    completedLabel: string,
    colors: { inProgress: string; completed: string }
): FrictionBreakdownSeries[] {
    return [
        {
            key: "inProgress",
            label: inProgressLabel,
            color: colors.inProgress,
        },
        {
            key: "completed",
            label: completedLabel,
            color: colors.completed,
        },
    ];
}
